import { computeDistance, MPOST, POST, stringToPosition } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { Package } from 'types/Package.ts';
import type { Shipment } from 'types/Shipment.ts';
import type { Tracker } from 'types/Tracker.ts';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Select } from 'ui/control/Select.tsx';
import { useConfirm } from '#admin/component/DialogProvider.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  shipment: Shipment;
  pkg: Package;
  onSave: (data: any) => void;
  onClose: () => void;
  isOpen: boolean;
};

export const TrackerAssignmentDialog: React.FC<Props> = ({ shipment, pkg, onSave, onClose, isOpen }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { data: trackers, mutate: mutateTrackers } = useSWR<Tracker[]>(
    ['/tracker/list', { sort: [['metrics_ex_id', 'asc']], shipment_id: shipment.id, owner_id: shipment.owner.owner_id }],
    POST,
  );

  const validTrackers = React.useMemo(() => {
    const validTrackers = { none: t('options.none') };
    if (!trackers) return validTrackers;
    const sortedTrackers = trackers.sort((a, b) => a.metrics_ex_id.localeCompare(b.metrics_ex_id));
    for (const tracker of sortedTrackers) {
      if (tracker.package_id && tracker.package_id !== pkg.id) continue;
      validTrackers[tracker.id] = `${tracker.metrics_ex_id} (${tracker.description})`.toUpperCase();
    }
    return validTrackers;
  }, [trackers, pkg, t]);

  const formMethods = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      trackerId: pkg.tracker?.id || 'none',
    },
  });

  const { handleSubmit, formState, watch } = formMethods;
  const selectedId = watch('trackerId');
  const { trigger, isMutating, error } = useSWRMutation(selectedId === 'none' ? '/tracker/unassign' : '/tracker/assign', MPOST);

  const handleSave = async ({ trackerId }) => {
    // confirm unassignment of current tracker
    if (trackerId === 'none' && !(await confirm(t('dialogs.tracker.unassignConfirm')))) return;

    // before assigning new tracker, run some checks
    if (trackerId !== 'none') {
      const targetTracker = trackers?.find((tracker) => tracker.id === trackerId) as Tracker;
      if (!targetTracker || !trackers) return;

      if (!targetTracker.last_measured_at && !(await confirm(t('dialogs.tracker.noMetricsConfirm')))) return;

      const lastMetricAgeInHours = !targetTracker.last_measured_at ? Number.POSITIVE_INFINITY : (Date.now() - new Date(targetTracker.last_measured_at).getTime()) / 3600_000;
      if (lastMetricAgeInHours > 24 && !(await confirm(t('dialogs.tracker.noPositionConfirm')))) return;

      const sameShipmentTrackers = trackers.filter((tracker) => tracker.shipment_id === pkg.shipment_id);
      for (const tracker of sameShipmentTrackers) {
        if (!targetTracker?.position) continue;
        if (!tracker?.position) continue;

        // confirm assignment if trackers are more than 100 meters apart
        const distance = computeDistance(stringToPosition(targetTracker.position), stringToPosition(tracker.position));
        if (distance > 100) {
          if (!(await confirm(t('dialogs.tracker.distanceWarning')))) return;
          else break;
        }
      }
    }

    await trigger(selectedId === 'none' ? { tracker_id: pkg.tracker?.id } : { tracker_id: trackerId, package_id: pkg.id });
    void mutateTrackers();
    onSave({ tracker_id: trackerId, package_id: pkg.id });
  };

  if (trackers === undefined) return <Spinner centered={true} />;

  return (
    <Modal key={pkg.id} title={<h4>{t('dialogs.tracker.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex w-[450px] flex-col gap-2.5" onSubmit={handleSubmit(handleSave)}>
          <div className="grid grid-cols-[100px_min-content] gap-2">
            <span>{t('labels.orderNumber')}:</span>
            <span>{shipment.logistics_ex_id}</span>
            <span>{t('labels.packageId')}:</span>
            <span>{pkg.logistics_ex_id}</span>
          </div>
          <div className="mt-6 flex flex-col gap-2">
            <Select name={'trackerId'} label={t('labels.trackerId')} options={validTrackers} registerOptions={{ required: true }} />
          </div>

          <div className="mt-4 flex gap-5">
            <Button disabled={isMutating} className="blue-outlined" onClick={onClose}>
              {t('actions.back')}
            </Button>
            <Button
              type="submit"
              disabled={formState.isSubmitting || !formState.isValid || !formState.isDirty}
              loading={formState.isSubmitting}
              className="blue flex items-center justify-center"
            >
              {t('actions.save')}
            </Button>
          </div>
          {error && (
            <span className="text-center text-red-500">
              <p>{t('dialogs.tracker.error.saving')}</p>
              <p>{getTranslatedError(error, t) || t('dialogs.tracker.error.tryAgain')}</p>
            </span>
          )}
        </form>
      </FormProvider>
    </Modal>
  );
};
